import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.drtoolsuk.com';
const SITE_NAME = 'Dr Tools';
const DEFAULT_DESC =
  'Dr Tools — UK trade specialists. Cordless drills, safety kit, plumbing, electrical, decorating and building supplies with bulk trade pricing, next-day delivery and open credit accounts for builders and retailers.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-cover.jpg`;

/**
 * Reusable SEO block. Every page should render this once (near the top of its JSX).
 * All props are optional — sensible sitewide defaults are used when omitted.
 *
 *   <SEO
 *     title="Cordless Drills"
 *     description="Shop cordless drills…"
 *     path="/category/tools"
 *     image="…"
 *     noindex
 *     jsonLd={[productSchema, breadcrumbSchema]}
 *   />
 */
const SEO = ({
  title,
  description = DEFAULT_DESC,
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = [],
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Trade-price tools, materials & site essentials`;
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`.replace(/\/+$/, '') || SITE_URL;
  // Ensure og:image is a fully-qualified URL — social crawlers reject relative paths.
  let ogImage = image || DEFAULT_OG_IMAGE;
  if (ogImage && !/^https?:\/\//i.test(ogImage) && !ogImage.startsWith('data:')) {
    ogImage = DEFAULT_OG_IMAGE;
  }
  const schemas = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

// -------- JSON-LD builders (pure, no side-effects) --------
export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  sameAs: ['https://www.instagram.com/drtoolsuk'],
});

export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const breadcrumbSchema = (trail) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: (trail || []).map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: crumb.name,
    item: crumb.path ? `${SITE_URL}${crumb.path.startsWith('/') ? crumb.path : `/${crumb.path}`}` : undefined,
  })),
});

export const productSchema = (product, opts = {}) => {
  if (!product) return null;
  const slug = product.slug || product.id;
  const url = `${SITE_URL}/products/${slug}`;
  const availability = (product.stock ?? 0) > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.images?.length ? product.images : [product.image].filter(Boolean),
    description: (product.description || '').replace(/<[^>]+>/g, '').slice(0, 500),
    sku: product.sku,
    mpn: product.part_no || product.sku,
    brand: {
      '@type': 'Brand',
      name: product.brand || SITE_NAME,
    },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'GBP',
      price: Number(Number(opts.price ?? product.price).toFixed(2)),
      availability,
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: SITE_NAME },
    },
  };
  if (product.rating > 0 && product.reviews > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(product.rating).toFixed(1),
      reviewCount: product.reviews,
      bestRating: 5,
      worstRating: 1,
    };
  }
  return schema;
};

export { SITE_URL, SITE_NAME };
export default SEO;
